/**
 * @file content-engagement.schema.ts
 * @description Define o schema Zod para a entidade 'ContentEngagement'.
 * Cada documento na coleção 'contentEngagement' representa uma sessão
 * de interação de um usuário com uma peça de conteúdo (vídeo, módulo, curso),
 * capturando métricas granulares de tempo de tela e frequência de acesso.
 *
 * Este schema opera em paralelo ao 'ContentProgress', que rastreia progresso
 * binário (assistiu/não assistiu). O 'ContentEngagement' captura dados temporais
 * detalhados para analytics e dashboards administrativos.
 */

import { z } from 'zod';

// --- Enums ---

/**
 * Tipos de conteúdo que podem gerar eventos de engajamento.
 */
const EngagementContentTypeEnum = z.enum(['VIDEO', 'MODULE', 'COURSE']);

// --- Main Schema ---

/**
 * Schema para um evento de sessão de engajamento.
 * Cada documento representa uma sessão contínua de visualização.
 *
 * O mobile gera um `sessionId` ao abrir o conteúdo e envia heartbeats periódicos
 * (a cada ~30s) com o tempo de tela acumulado. Ao fechar ou sair do conteúdo,
 * envia o flush final com `sessionEndedAt`.
 */
const ContentEngagementSchema = z.object({
  id: z.string(),

  // Identificação do usuário e contexto
  userId: z.string(),
  pregnancyId: z.string().nullable(),
  partnerId: z.string().nullable(),

  // Contexto do conteúdo
  contentType: EngagementContentTypeEnum,
  contentId: z.string(),          // videoFilename, moduleId, courseId
  courseId: z.string(),            // sempre presente para contexto hierárquico
  moduleId: z.string().nullable(), // null se contentType === 'COURSE'
  trailId: z.string().nullable(),  // se acessado via trilha

  // Métricas da sessão
  sessionId: z.string(),           // UUID gerado no mobile por sessão de visualização
  screenTimeSeconds: z.number().int().min(0), // tempo de tela acumulado nesta sessão

  // Timestamps da sessão
  sessionStartedAt: z.date(),
  sessionEndedAt: z.date().nullable(),

  // Auditoria
  createdAt: z.date(),
  updatedAt: z.date(),
});


// --- Inferred TypeScript Types ---

type ContentEngagement = z.infer<typeof ContentEngagementSchema>;
type EngagementContentType = z.infer<typeof EngagementContentTypeEnum>;


// --- Public Exports ---

export {
  ContentEngagementSchema,
  EngagementContentTypeEnum,
};

export type {
  ContentEngagement,
  EngagementContentType,
};
